import React from 'react'

import Pill from './Pill'

/**
 * Canonical "Forecast Posture" block used across CompanyDetail's memo
 * hero, the Companies / CompanyGroup cards, and (eventually) the
 * Workspace company sub-cards. Centralising the layout means a copy
 * change ("Forecast Posture" → "Stance") only needs to land here.
 *
 * Visual structure:
 *
 *   FORECAST POSTURE                                 [right slot]
 *   Big headline (most likely case in PM voice)
 *   [Stance pill]
 *   Assessment paragraph
 *   [most likely case · next proof point · dominant risk]
 *
 * Pass `compact` for the card-list variant (no kicker / no padding) and
 * `dense` for the index-card variant used inside Companies tiles.
 */
export type PostureProps = {
  headline?: string | null
  /** "Stance" pill copy (e.g. "Constructive — earnings catalyst"). */
  label?: string | null
  /** Stance summary / assessment paragraph. */
  summary?: string | null
  mostLikelyCase?: { label?: string | null; probability?: number | null } | null
  nextProofPoint?: string | null
  dominantRisk?: string | null
  /** Slot rendered to the right of the kicker (typically a freshness pill). */
  rightSlot?: React.ReactNode
  /** Tighter horizontal padding and no card background. Used inside
   *  existing list cards (Companies, CompanyGroup) that already supply
   *  their own padding. */
  compact?: boolean
  /** Hide the "Forecast Posture" kicker when context already implies it. */
  hideKicker?: boolean
}

function caseTag(c: PostureProps['mostLikelyCase']): string | null {
  if (!c) return null
  const label = typeof c.label === 'string' ? c.label.trim() : ''
  const p = typeof c.probability === 'number' ? c.probability : null
  if (!label) return null
  if (p === null) return label
  return `${label} ${Math.round(p * 100)}%`
}

export default function Posture({
  headline,
  label,
  summary,
  mostLikelyCase,
  nextProofPoint,
  dominantRisk,
  rightSlot,
  compact = false,
  hideKicker = false,
}: PostureProps) {
  const tag = caseTag(mostLikelyCase)
  const wrapperStyle: React.CSSProperties = compact
    ? {}
    : {
        padding: 20,
        borderRadius: 22,
        border: '1px solid var(--border)',
        background:
          'radial-gradient(circle at top left, rgba(99,102,241,0.18), transparent 32%), linear-gradient(135deg, rgba(15,23,42,0.04), rgba(99,102,241,0.05))',
      }

  return (
    <div className="posture" style={wrapperStyle}>
      {!hideKicker && (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 850,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              color: 'var(--accent, #6366f1)',
            }}
          >
            Forecast Posture
          </div>
          {rightSlot ? <div>{rightSlot}</div> : null}
        </div>
      )}
      {headline ? (
        <h3
          style={{
            margin: hideKicker ? '0 0 4px' : '6px 0 8px',
            fontSize: compact ? 16 : 24,
            lineHeight: 1.2,
            fontWeight: 750,
          }}
        >
          {headline}
        </h3>
      ) : null}
      {label ? (
        <Pill tone="accent" style={{ marginBottom: 10 }}>
          {label}
        </Pill>
      ) : null}
      {summary ? (
        <div style={{ maxWidth: 920, fontSize: compact ? 13 : 15, lineHeight: 1.55, color: 'var(--muted)' }}>
          {summary}
        </div>
      ) : null}
      {(tag || nextProofPoint || dominantRisk) && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {tag ? <Pill tone="accent">{tag}</Pill> : null}
          {nextProofPoint ? <Pill tone="warn">Next: {nextProofPoint}</Pill> : null}
          {dominantRisk ? <Pill tone="bad">Risk: {dominantRisk}</Pill> : null}
        </div>
      )}
    </div>
  )
}
